import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, logout } from "../loginSlice";
import "./login.css";
import { setBackButtonUrl } from "../uiSlice";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken, clearSession } from "../../admin/sessionSlice";
import { apiUrl } from "../../utils/apiUrl";
import { s3ThemeAssetUrl } from "../../utils/themeAssets";
import { setJury } from "../jury/jurySlice";
import Swal from "sweetalert2";

function safeRedirectUrl(url) {
  const fallback =
    process.env.REACT_APP_BASE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "/") ||
    "/";
  if (!url || typeof url !== "string") return fallback;
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return fallback;
  }
}

function Login() {
  const dispatch = useDispatch();
  const { status, user, error } = useSelector((state) => state.auth);
  const { status: themeStatus, data: themeData, error: themeError } = useSelector(
    (state) => state.theme
  );
  const adminToken = useSelector(selectAdminToken);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";
  const isFromMobileApp = searchParams.get("fromMobileApp") === "true";
  const mobileAppQuery = isFromMobileApp ? "?fromMobileApp=true" : "";

  // idle | checking | done
  const [routeCheck, setRouteCheck] = useState("idle");
  const routeAttempted = useRef(false);

  // Run login once per distinct credential set. Re-running on every query-string change would
  // log out and re-submit an already used OTP (e.g. after the URL is cleaned up below).
  const handledLoginKey = useRef(null);
  const strippedCredentials = useRef(false);
  useEffect(() => {
    const otp = searchParams.get("otp");
    const demo = searchParams.get("demo");
    const save = searchParams.get("save");
    const key = `${otp || ""}|${demo || ""}|${save || ""}`;
    if (key === handledLoginKey.current) return;
    if (key === "||" && strippedCredentials.current) {
      handledLoginKey.current = key;
      return;
    }
    handledLoginKey.current = key;
    if (otp || demo) {
      dispatch(logout());
      dispatch(clearSession());
      routeAttempted.current = false;
      setRouteCheck("idle");
    }
    dispatch(loginUser());
  }, [dispatch, searchParams]);

  // After a successful OTP/demo login, drop the one-time credentials from the URL so a refresh
  // restores the saved session instead of replaying the OTP.
  useEffect(() => {
    if (status !== "succeeded") return;
    if (!searchParams.get("otp") && !searchParams.get("demo")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("otp");
    next.delete("demo");
    strippedCredentials.current = true;
    setSearchParams(next, { replace: true });
  }, [status, searchParams, setSearchParams]);

  useEffect(() => {
    if (status === "succeeded" && user && isAdmin) {
      navigate("/admin");
    }
  }, [status, user, isAdmin, navigate]);

  useEffect(() => {
    if (status === "succeeded" && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  // After player login: jury → /jury/vote; no more upload slots → /gallery; else Start
  useEffect(() => {
    if (isAdmin) return;
    if (status !== "succeeded" || themeStatus !== "succeeded" || !user) return;
    if (!adminToken) return;
    if (routeAttempted.current) return;

    routeAttempted.current = true;
    setRouteCheck("checking");

    let cancelled = false;
    (async () => {
      try {
        const juryLoginMethod =
          String(themeData?.jury_login_method || "").trim().toLowerCase() === "email"
            ? "email"
            : "employee_id";
        const employeeId = String(user.employeeId || "").trim();
        const userEmail = String(user.email || "").trim();
        const juryBody =
          juryLoginMethod === "email"
            ? userEmail
              ? { email: userEmail }
              : null
            : employeeId
              ? { employee_id: employeeId }
              : null;
        if (juryBody) {
          const juryResp = await fetch(apiUrl("/juryLogin"), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify(juryBody),
          });
          const juryJson = await juryResp.json().catch(() => ({}));
          if (cancelled) return;
          if (juryResp.ok && juryJson?.success && juryJson?.jury?.id) {
            dispatch(setJury(juryJson.jury));
            navigate("/jury/vote", { replace: true });
            return;
          }
        }

        if (user.userId) {
          const statusResp = await fetch(apiUrl("/spotGameStatus"), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({ userId: user.userId }),
          });
          const statusJson = await statusResp.json().catch(() => ({}));
          if (cancelled) return;
          // Only force gallery when player already uploaded AND cannot upload more
          // (respects Multiple upload + limit from admin).
          if (
            statusResp.ok &&
            statusJson?.hasUpload &&
            statusJson?.canUploadMore === false
          ) {
            navigate("/gallery", { replace: true });
            return;
          }
        }
      } catch {
        /* continue as new player */
      }
      if (!cancelled) setRouteCheck("done");
    })();

    return () => {
      cancelled = true;
    };
  }, [status, themeStatus, themeData?.jury_login_method, user, adminToken, isAdmin, dispatch, navigate]);

  useEffect(() => {
    if (status !== "failed") return;
    const msg = error ? String(error) : "Session expired";
    const redirect = safeRedirectUrl(user?.backButtonRedirect);
    const title =
      msg.includes("OTP is required") || msg.includes("No saved user")
        ? "Login required"
        : "Session Expired!";
    Swal.fire(title, msg, "error").then(() => {
      window.location.href = redirect;
    });
  }, [status, error, user]);

  const retryThemeLoad = () => {
    dispatch(fetchThemeData({ themeId: null }));
  };

  const mayRedirect =
    status === "succeeded" &&
    themeStatus === "succeeded" &&
    !isAdmin &&
    routeCheck !== "done";

  const showLoader =
    status === "loading" || themeStatus === "loading" || mayRedirect;

  if (showLoader) {
    return (
      <div className="login-main-container">
        <div className="quiz-loader-container">
          <div className="quiz-loader"></div>
        </div>
      </div>
    );
  }

  if (status === "succeeded" && user && themeStatus === "failed") {
    return (
      <div className="login-main-container">
        <p className="login-theme-error">
          Could not load game theme. Make sure the backend is running on port 5001.
          {themeError ? ` (${themeError})` : ""}
        </p>
        <button type="button" className="begin-play-btn" onClick={retryThemeLoad}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="login-main-container">
      {themeData && (
        <img
          src={s3ThemeAssetUrl(themeData.logo)}
          className="login-logo"
          alt="Game Logo"
        />
      )}
      {user && status === "succeeded" && themeStatus === "succeeded" && themeData && (
        <div>
          <Link
            to={`/how-to-play${mobileAppQuery}`}
            className="begin-play-btn"
            style={{
              ...(themeData?.button_backgroundcolor && {
                backgroundColor: themeData.button_backgroundcolor,
              }),
              ...(themeData?.button_color && { color: themeData.button_color }),
              ...(themeData?.button_bordercolor && {
                borderColor: themeData.button_bordercolor,
              }),
              ...(themeData?.button_color && {
                border: `2px solid ${themeData.button_color}`,
              }),
            }}
          >
            Start
          </Link>
        </div>
      )}
    </div>
  );
}

export default Login;
